import { supabase, isConfigured } from './supabase';
import { DEFAULT_STUDIO_NEWS_ITEMS } from '../features/client-portal/components/ClientPortalStudioNews';

// Editor de Novedades del portal → tabla public.studio_news (migración 169 + 170).
// Patrón GEMELO de landing_assets (lib/landingAssets.js): whitelist de columnas + normalize
// (row↔editor). La diferencia deliberada está en el uploader (ver abajo).

export const STUDIO_NEWS_BUCKET = 'studio-news';
export const STUDIO_NEWS_MAX_BYTES = 5 * 1024 * 1024; // 5 MB — igual que el bucket (170)
export const STUDIO_NEWS_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];

// Columnas REALES de studio_news. El payload se filtra contra este set → nunca mandamos
// claves inexistentes (mismo blindaje que filterLandingAssetPayload).
export const STUDIO_NEWS_COLUMNS = new Set([
  'id', 'title', 'summary', 'body',
  'cover_bucket', 'cover_path', 'cover_url', 'cover_alt', 'cover_focal_x', 'cover_focal_y',
  'banner_bucket', 'banner_path', 'banner_url', 'banner_alt', 'banner_focal_x', 'banner_focal_y',
  'tag', 'cta', 'tone', 'accent', 'icon',
  'sort_order', 'published', 'published_at', 'created_by', 'created_at', 'updated_at',
]);

function clampPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 50;
  return Math.min(100, Math.max(0, Math.round(n)));
}

function safeFileName(value = 'foto') {
  return String(value || 'foto')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '')
    .toLowerCase() || 'foto';
}

// Row (snake_case) → objeto del editor (camelCase). INCLUYE cta y los alt aunque no tengan
// UI: así hacen round-trip y NO se pierden al editar una novedad del seed (el bug que la 169
// casi comete con cta, mordiendo desde el editor).
export function normalizeStudioNewsRow(row = {}) {
  return {
    id: row.id || '',
    title: row.title || '',
    summary: row.summary || '',
    body: row.body || '',
    published: row.published === true,
    published_at: row.published_at || null,
    order: Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : 0,
    coverUrl: row.cover_url || '',
    coverBucket: row.cover_bucket || STUDIO_NEWS_BUCKET,
    coverPath: row.cover_path || '',
    coverAlt: row.cover_alt || '',
    coverFocalX: clampPct(row.cover_focal_x),
    coverFocalY: clampPct(row.cover_focal_y),
    bannerUrl: row.banner_url || '',
    bannerBucket: row.banner_bucket || STUDIO_NEWS_BUCKET,
    bannerPath: row.banner_path || '',
    bannerAlt: row.banner_alt || '',
    bannerFocalX: clampPct(row.banner_focal_x),
    bannerFocalY: clampPct(row.banner_focal_y),
    tag: row.tag || '',
    cta: row.cta || '',            // aditivo sin UI — round-trip
    tone: row.tone || '',
    accent: row.accent || '',
    icon: row.icon || '',
    created_by: row.created_by || null,
    created_at: row.created_at || null,
    updated_at: row.updated_at || null,
  };
}

// Objeto del editor (camelCase) → row (snake_case), filtrado a columnas reales.
// NO incluye id/created_by/created_at/published_at (los gestionan los métodos de AppContext).
export function buildStudioNewsPayload(item = {}) {
  const payload = {
    title: String(item.title || '').trim(),
    summary: item.summary ? String(item.summary) : null,
    body: item.body ? String(item.body) : null,
    cover_bucket: STUDIO_NEWS_BUCKET,
    cover_path: item.coverPath || null,
    cover_url: item.coverUrl || null,
    cover_alt: item.coverAlt || null,       // null a propósito (deuda a11y, capítulo "presentación")
    cover_focal_x: clampPct(item.coverFocalX),
    cover_focal_y: clampPct(item.coverFocalY),
    banner_bucket: STUDIO_NEWS_BUCKET,
    banner_path: item.bannerPath || null,
    banner_url: item.bannerUrl || null,
    banner_alt: item.bannerAlt || null,
    banner_focal_x: clampPct(item.bannerFocalX),
    banner_focal_y: clampPct(item.bannerFocalY),
    tag: item.tag || null,
    cta: item.cta || null,
    tone: item.tone || null,
    accent: item.accent || null,
    icon: item.icon || null,
    sort_order: Number.isFinite(Number(item.order)) ? Number(item.order) : 0,
    published: item.published === true,
  };
  return Object.fromEntries(Object.entries(payload).filter(([key]) => STUDIO_NEWS_COLUMNS.has(key)));
}

// Semilla del editor cuando NO hay BD (local/seed/demo): las 8 novedades hardcodeadas mapeadas
// a la forma del editor. Espeja lo que el portal muestra hoy.
export function seedStudioNewsItems() {
  return DEFAULT_STUDIO_NEWS_ITEMS.map((item, index) => normalizeStudioNewsRow({
    id: item.id || `news-${index}`,
    title: item.title,
    summary: item.copy,
    body: item.body,
    tag: item.tag,
    cta: item.cta,
    tone: item.tone,
    accent: item.accent,
    icon: item.icon,
    cover_url: item.imageUrl || null,
    sort_order: index,
    published: true,
  }));
}

// ── UPLOADER DEDICADO ────────────────────────────────────────────────────────
// DUPLICA A PROPÓSITO el núcleo de uploadLandingAssetFile (landingAssets.js:432).
// Decisión de David (contra la recomendación de compartir un primitivo): el build NO prueba
// que un uploader suba, así que verificar una extracción compartida obligaría a re-validar la
// Landing/Catálogo visual — producción ajena a este capítulo. Asimetría: copia mal = ~12
// líneas duplicadas; extracción mal = el uploader de la landing roto en prod. Por eso NO se
// toca landingAssets.js. Diferencias con landing: bucket 'studio-news' y path UUID (no
// timestamp) — mitiga el residual del bucket público de la 170.
export async function uploadStudioNewsFile(file, { name } = {}) {
  if (!file) throw new Error('Selecciona una imagen para subir.');
  if (file.type && !STUDIO_NEWS_MIME_TYPES.includes(file.type)) {
    throw new Error('Usa una imagen JPG, PNG, WEBP o AVIF.');
  }
  if (file.size > STUDIO_NEWS_MAX_BYTES) {
    throw new Error('La foto pesa más de 5 MB. Sube una versión más liviana.');
  }
  if (!isConfigured) throw new Error('Supabase no está configurado para subir imágenes.');

  const uuid = crypto?.randomUUID?.() || `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
  const path = `${uuid}-${safeFileName(name || file.name || 'foto')}`;
  const { error } = await supabase.storage
    .from(STUDIO_NEWS_BUCKET)
    .upload(path, file, { contentType: file.type || 'image/jpeg', cacheControl: '31536000', upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from(STUDIO_NEWS_BUCKET).getPublicUrl(path);
  return { bucket: STUDIO_NEWS_BUCKET, path, url: data?.publicUrl || '' };
}

// Borra objetos del bucket (best-effort). Se usa al REEMPLAZAR, QUITAR y BORRAR — nuestro
// capítulo no engorda el basural del bucket.
export async function removeStudioNewsObjects(paths = []) {
  const clean = (Array.isArray(paths) ? paths : [paths]).filter(Boolean);
  if (!clean.length || !isConfigured) return;
  try {
    await supabase.storage.from(STUDIO_NEWS_BUCKET).remove(clean);
  } catch (error) {
    console.warn('[studioNews] No se pudo borrar del storage:', clean, error?.message || error);
  }
}

// URL pública de la portada para el portal: la guardada, o derivada del path por robustez.
function portalImageUrl(item = {}) {
  if (item.coverUrl) return item.coverUrl;
  if (item.coverPath && isConfigured) {
    const { data } = supabase.storage.from(item.coverBucket || STUDIO_NEWS_BUCKET).getPublicUrl(item.coverPath);
    return data?.publicUrl || '';
  }
  return '';
}

function portalBannerUrl(item = {}) {
  if (item.bannerUrl) return item.bannerUrl;
  if (item.bannerPath && isConfigured) {
    const { data } = supabase.storage.from(item.bannerBucket || STUDIO_NEWS_BUCKET).getPublicUrl(item.bannerPath);
    return data?.publicUrl || '';
  }
  return '';
}

// ── EL PUNTO FOCAL VIAJA HASTA EL PORTAL ─────────────────────────────────────────────
// 🔴 POR QUE EXISTE: el editor de Novedades da al staff un punto focal ARRASTRABLE para
// dos fotos (portada y banner) y le muestra previews que su propio codigo declara «fieles
// a las dos vistas» — `AdminNovedades:64` dice literalmente «Fiel a ClientPortal:2628».
// No lo era: la proyeccion al portal descartaba `coverFocal*`, `banner*` y `bannerFocal*`,
// y las DOS superficies pintaban `center / cover` quemado. El staff encuadraba una cara,
// veia el encuadre correcto en Admin, publicaba, y el cliente recibia otro recorte.
//
// Ademas el hero del articulo abierto usaba la PORTADA: la foto de banner no llegaba nunca
// al portal, aunque el editor la pide, la sube y la previsualiza.
//
// La regla de caida es la del editor, replicada al pie (`AdminNovedades:75-85`): el banner
// usa su foto con SU focal; si no hay banner cae a la portada CON EL FOCAL DE LA PORTADA
// —no con el del banner, que no se configuro—; si no hay ninguna, el degradado.
function posicionFocal(x, y) {
  return `${clampPct(x)}% ${clampPct(y)}%`;
}

// ╔═ VERDAD DE LOS DATOS, SEPARADA DE LA PRESENTACIÓN ═══════════════════════════════╗
// ║ Resuelve QUÉ hay, no CÓMO se muestra. Home y Novedades deciden por su cuenta qué  ║
// ║ estados renderizan; esta función no sabe de superficies.                          ║
// ║                                                                                   ║
// ║ 🔴 EXISTE POR UN BUG REAL: el portal hacía                                        ║
// ║      studioNewsError ? DEFAULT_STUDIO_NEWS_ITEMS : proyeccion(filas)               ║
// ║   Un fallo de red o de esquema mostraba las OCHO novedades de ejemplo del código   ║
// ║   como si fueran contenido editorial verdadero — y peor: si ya había novedades     ║
// ║   reales cargadas, las DESCARTABA para mostrar las inventadas.                     ║
// ║                                                                                   ║
// ║   CONTRATO: dato real → se muestra · ausencia real → vacío · fallo → estado        ║
// ║   distinguible. NUNCA contenido inventado.                                        ║
// ║                                                                                   ║
// ║ Híbrido deliberado (decisión de David):                                           ║
// ║   · con datos reales en memoria → STALE-WHILE-ERROR: se conservan. Borrar datos    ║
// ║     válidos por un fallo transitorio es la contracara del bug que se arregla.      ║
// ║   · sin datos → FAIL-CLOSED: estado de error. "Conservar" la nada seria            ║
// ║     indistinguible del vacío legítimo.                                             ║
// ║                                                                                   ║
// ║ Por qué `filas.length > 0` alcanza como prueba de "hubo carga exitosa": en         ║
// ║ producción el ÚNICO camino que llena `data.studioNews` es la carga exitosa         ║
// ║ (AppContext:2819). El seed de las 8 (`:2807`) solo corre con `!isConfigured ||     ║
// ║ USE_SEED || demoMode`, y las mutaciones del editor solo existen en Admin.          ║
// ║ Verificado mutación por mutación. Por eso NO hace falta un estado nuevo.           ║
// ╚═══════════════════════════════════════════════════════════════════════════════════╝
export function resolverNovedadesDelPortal({ filas = [], error = false, cargando = false } = {}) {
  const items = studioNewsToPortalItems(filas || []);

  // Con datos reales, un intento en curso NO borra lo que ya se ve.
  if (cargando && !items.length) return { items: [], estado: 'cargando' };
  if (error) return items.length
    ? { items, estado: 'desactualizado' }   // stale-while-error
    : { items: [], estado: 'error' };       // fail-closed
  return items.length ? { items, estado: 'ok' } : { items: [], estado: 'vacio' };
}

// ── POLÍTICA DE PRESENTACIÓN DEL HOME ────────────────────────────────────────────────
// Separada a proposito de `resolverNovedadesDelPortal`: aquella resuelve QUÉ hay (verdad de
// datos); esta resuelve SI esta superficie lo muestra.
//
// 🔴 EXISTE POR UN BUG REAL: el carrusel del Home se renderizaba con `items.length > 0` a
// secas, sin mirar `portal_show_news`. La pestaña, el menú y la redirección SÍ lo miraban;
// el Home no. Medido en produccion el 2026-08-10 (UTC de la BD): `portal_show_news = false` con **8
// novedades publicadas** — el estudio las tenia apagadas y el Home las mostraba igual.
//
// SEMÁNTICA CONFIRMADA POR DAVID: el ajuste significa **mostrar u ocultar las novedades en
// TODO el portal del cliente**, no solo la pestaña. (El titulo del toggle y el aviso de Admin
// —«se veran cuando la actives»— ya prometian eso; la descripcion que hablaba solo de «la
// pestaña» era el texto desalineado.)
//
// ⚠️ EL TOGGLE GOBIERNA PRESENTACIÓN, NO DATOS. No vacia ni altera los items: apagar oculta,
// encender vuelve a mostrar de inmediato lo que ya estaba cargado, sin recargar nada.
export function debeMostrarNovedadesEnHome({ showStudioNews = false, items = [] } = {}) {
  return Boolean(showStudioNews) && (items?.length || 0) > 0;
}

// Items del editor (data.studioNews, camelCase) → forma que consumen los lectores del portal
// (PortalNewsCarousel + ClientPortalStudioNews): { id, icon, tag, title, copy, cta, tone,
// accent, body, imageUrl }. Filtra PUBLICADAS y ordena por sort_order. NO se reusa
// normalizeStudioNewsRow (esa da la forma del editor: coverUrl/summary/order).
export function studioNewsToPortalItems(items = []) {
  return (Array.isArray(items) ? items : [])
    .filter(item => item && item.published === true)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(item => ({
      id: item.id,
      icon: item.icon || 'sparkles',
      tag: item.tag || '',
      title: item.title || '',
      copy: item.summary || '',
      cta: item.cta || '',
      tone: item.tone || '#eb6154',
      accent: item.accent || '#f09e9e',
      body: item.body || '',
      imageUrl: portalImageUrl(item),
      imagePosition: posicionFocal(item.coverFocalX, item.coverFocalY),
      bannerImageUrl: portalBannerUrl(item) || portalImageUrl(item),
      bannerImagePosition: portalBannerUrl(item)
        ? posicionFocal(item.bannerFocalX, item.bannerFocalY)
        : posicionFocal(item.coverFocalX, item.coverFocalY),
    }));
}
